import { Member, MemberAlias } from '@nextforce/api'
import { makeStyles } from '@rneui/themed'
import { CollapsibleSectionHeader } from 'chat/members/views/collapsible_section_header'
import { t } from 'app/i18l'
import React, { useCallback, useEffect, useState } from 'react'
import {
  SectionList,
  SectionListData,
  SectionListRenderItem,
} from 'react-native'

export type Section = {
  title: string
  data: Member[]
  type: MemberAlias
}

interface Props {
  renderItem?: SectionListRenderItem<Member> | undefined
  renderSectionHeader?: (
    section: SectionListData<Member, Section>,
    isCollapsed: boolean,
    onToggleCollapse: () => void
  ) => React.JSX.Element | null
  members: readonly Member[] | undefined
  extraData?: any
  defaultCollapsedSections?: MemberAlias[] | undefined
}

export function MembersList({
  renderItem,
  renderSectionHeader,
  members,
  extraData,
  defaultCollapsedSections,
}: Props) {
  const styles = useStyles()

  const [collapsedSections, setCollapsedSections] = useState<
    Set<MemberAlias> | undefined
  >()

  useEffect(() => {
    if (defaultCollapsedSections && collapsedSections === undefined) {
      setCollapsedSections(new Set(defaultCollapsedSections))
    }
  }, [defaultCollapsedSections, collapsedSections])

  const toggleSectionCollapse = useCallback((sectionType: Section['type']) => {
    setCollapsedSections(prev => {
      const newSet = new Set(prev)
      if (newSet.has(sectionType)) {
        newSet.delete(sectionType)
      } else {
        newSet.add(sectionType)
      }
      return newSet
    })
  }, [])

  const {
    seniorManagers,
    managers,
    staff,
    guests,
    crew,
    others,
    crewNewHires,
  } = categorizeMembers(members ?? [])

  const defaultSections: Section[] = [
    {
      title: t('memberAlias.seniorManagers').toUpperCase(),
      data: seniorManagers,
      type: 'allSeniorManagers',
    },
    {
      title: t('memberAlias.managers').toUpperCase(),
      data: managers,
      type: 'allManagers',
    },
    {
      title: t('memberAlias.staff').toUpperCase(),
      data: staff,
      type: 'allStaff',
    },
    {
      title: t('memberAlias.guests').toUpperCase(),
      data: guests,
      type: 'allGuests',
    },
    {
      title: t('memberAlias.newHires').toUpperCase(),
      data: crewNewHires,
      type: 'crewNewHires',
    },
    { title: t('memberAlias.crew').toUpperCase(), data: crew, type: 'allCrew' },
    {
      title: t('memberAlias.admins').toUpperCase(),
      data: others,
      type: 'allAdmins',
    },
  ]

  // Filter sections based on collapsed state
  const sections: Section[] = defaultSections.map(section => {
    return {
      ...section,
      data: collapsedSections?.has(section.type) ? [] : section.data,
    }
  })

  return (
    <SectionList
      renderSectionHeader={({ section }) => {
        const isCollapsed = collapsedSections?.has(section.type) ?? false
        const onToggleCollapse = () => toggleSectionCollapse(section.type)

        const originalSection = defaultSections.find(
          s => s.type === section.type
        )
        const hasData = !!(
          originalSection?.data?.length && originalSection.data.length > 0
        )

        return hasData ? (
          renderSectionHeader ? (
            renderSectionHeader(section as any, isCollapsed, onToggleCollapse)
          ) : section.title ? (
            <CollapsibleSectionHeader
              title={section.title}
              isCollapsed={isCollapsed}
              onToggleCollapse={onToggleCollapse}
            />
          ) : null
        ) : null
      }}
      sections={sections}
      renderItem={renderItem}
      keyExtractor={item => String(item.id)}
      extraData={extraData}
      stickySectionHeadersEnabled={true}
      contentContainerStyle={styles.listContentContainer}
    />
  )
}

export function categorizeMembers(allMembers: readonly Member[]) {
  const seniorManagers: Member[] = []
  const managers: Member[] = []
  const staff: Member[] = []
  const guests: Member[] = []
  const crew: Member[] = []
  const others: Member[] = []
  const crewNewHires: Member[] = []

  allMembers.forEach(member => {
    if (isSeniorManager(member)) {
      seniorManagers.push(member)
    } else if (member.roles?.includes('manager')) {
      managers.push(member)
    } else if (member.roles?.includes('staff')) {
      staff.push(member)
    } else if (member.roles?.includes('guest')) {
      guests.push(member)
    } else if (member.roles?.includes('helper')) {
      // Add to crew section
      crew.push(member)
      // Also add to crewNewHires if they are a new hire
      if (member.isNewHire) {
        crewNewHires.push(member)
      }
    } else {
      others.push(member)
    }
  })

  return { seniorManagers, managers, staff, guests, crew, others, crewNewHires }
}

function isSeniorManager(member: Member) {
  if (!member.roles) {
    return false
  }

  return member.roles.some(role => role === 'observer' || role === 'admin')
}

const useStyles = makeStyles(theme => ({
  listContentContainer: {
    flexGrow: 1,
  },
  aliasRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    height: 50,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors?.grey5,
  },
  aliasText: {
    fontSize: 14,
  },
}))
